import { CalendarPlusIcon } from 'lucide-react-native';
import * as React from 'react';
import { Platform, View } from 'react-native';

import { IntegrationBadge } from '@/components/integrations/integration-badge';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import type { Event } from '@/lib/events';
import { downloadIcsCalendarEvent } from '@/lib/integrations/calendar-actions';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

type CalendarActionBadgesProps = Readonly<{
  event: Event;
  returnPath: string;
  timeZone?: string;
}>;

export function CalendarActionBadges({ event, returnPath, timeZone }: CalendarActionBadgesProps) {
  const calendarExportEnabled = useReleaseFeatureFlag('calendarExport') === true;
  const calendarSyncEnabled = useReleaseFeatureFlag('calendarSync') === true;
  const [icsError, setIcsError] = React.useState<string | null>(null);

  if (!calendarExportEnabled && !calendarSyncEnabled) {
    return null;
  }

  function exportIcs() {
    setIcsError(null);
    try {
      downloadIcsCalendarEvent(event);
    } catch (error) {
      setIcsError(error instanceof Error ? error.message : 'Could not export calendar file.');
    }
  }

  return (
    <View className="gap-2">
      <Text variant="label" className="text-muted-foreground">
        CALENDAR
      </Text>
      <View className="flex-row flex-wrap gap-2">
        {calendarSyncEnabled ? (
          <IntegrationBadge
            provider="google_calendar"
            mode="event"
            event={event}
            returnPath={returnPath}
            timeZone={timeZone}
          />
        ) : null}
        {calendarExportEnabled ? (
          <IntegrationBadge
            provider="apple_calendar"
            mode="event"
            event={event}
            returnPath={returnPath}
            timeZone={timeZone}
          />
        ) : null}
        {calendarExportEnabled && Platform.OS === 'web' ? (
          <Button className="h-11 px-3" onPress={exportIcs} size="sm" variant="secondary">
            <Icon as={CalendarPlusIcon} className="size-4 text-secondary-foreground" />
            <Text>ICS</Text>
          </Button>
        ) : null}
      </View>
      {icsError ? (
        <Text className="text-sm text-destructive" selectable>
          {icsError}
        </Text>
      ) : null}
    </View>
  );
}
